import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

import { useToast } from '@/hooks/use-toast';
import { ArrowLeft } from 'lucide-react';
import StarsBackground from '@/components/StarsBackground';
import { getErrorCategory, logError } from '@/utils/errorHandling';
import { signUpSchema, signInSchema } from '@/utils/authValidation';
import { authRateLimiter } from '@/utils/security';
import { supabase } from '@/integrations/supabase/client';

type Mode = 'login' | 'signup' | 'forgot';

const Auth = () => {
  const [mode, setMode] = useState<Mode>('login');
  const isLogin = mode === 'login';
  const isForgot = mode === 'forgot';
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    userType: 'user' as const,
    ambassadorMessage: ''
  });
  const [loading, setLoading] = useState(false);
  
  const { signIn, signUp } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email) {
      toast({ title: 'Enter your email', variant: 'destructive' });
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(formData.email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setLoading(false);
    if (error) {
      toast({ title: 'Could not send reset email', description: error.message, variant: 'destructive' });
      return;
    }
    toast({
      title: 'Check your email',
      description: "If an account exists for that address, we've sent a password reset link.",
    });
    setMode('login');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Rate limiting - 5 attempts per 15 minutes
    const rateLimitKey = isLogin ? 'signin' : 'signup';
    if (!authRateLimiter.recordAttempt(formData.email, false)) {
      const timeUntilUnlock = authRateLimiter.getTimeUntilUnlock(formData.email);
      const minutesRemaining = Math.ceil(timeUntilUnlock / 60000);
      toast({
        title: "Too Many Attempts",
        description: `Account temporarily locked. Try again in ${minutesRemaining} minutes.`,
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    try {
      if (isLogin) {
        // Validate sign-in data
        const validationResult = signInSchema.safeParse({
          email: formData.email,
          password: formData.password
        });

        if (!validationResult.success) {
          const firstError = validationResult.error.errors[0];
          toast({
            title: "Validation Error",
            description: firstError.message,
            variant: "destructive"
          });
          return;
        }

        const { error } = await signIn(validationResult.data.email, validationResult.data.password);
        if (error) {
          logError(error, { action: 'signin', email: formData.email });
          toast({
            title: "Sign In Failed",
            description: getErrorCategory(error),
            variant: "destructive"
          });
        } else {
          authRateLimiter.recordAttempt(formData.email, true); // Clear rate limit on success
          toast({
            title: "Success",
            description: "Logged in successfully!",
          });
          navigate('/dashboard');
        }
      } else {
        // Validate sign-up data
        const validationResult = signUpSchema.safeParse(formData);

        if (!validationResult.success) {
          const firstError = validationResult.error.errors[0];
          toast({
            title: "Validation Error",
            description: firstError.message,
            variant: "destructive"
          });
          return;
        }

        const validated = validationResult.data;
        const { error, isAmbassadorRequest } = await signUp(
          validated.email, 
          validated.password, 
          validated.firstName, 
          validated.lastName,
          validated.userType,
          validated.ambassadorMessage
        );
        if (error) {
          logError(error, { action: 'signup', userType: formData.userType });
          toast({
            title: "Sign Up Failed",
            description: getErrorCategory(error),
            variant: "destructive"
          });
        } else {
          authRateLimiter.recordAttempt(formData.email, true); // Clear rate limit on success
          if (isAmbassadorRequest) {
            toast({
              title: "Ambassador Request Submitted",
              description: "Your ambassador application has been submitted for review. You can sign in with your account while we process your request.",
            });
          } else {
            toast({
              title: "Success",
              description: "Account created successfully! Please check your email for verification.",
            });
          }
          navigate('/dashboard');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden bg-solr-space-darker">
      <div className="fixed inset-0 pointer-events-none" aria-hidden="true" role="presentation">
        <StarsBackground />
      </div>
      <div className="min-h-screen flex items-center justify-center p-4 relative z-10">
        <Card className="w-full max-w-md bg-solr-void-black/80 border-solr-nebula-purple backdrop-blur-sm relative">
          {/* Back Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
            className="absolute top-4 left-4 text-solr-starfield-white hover:bg-solr-nebula-purple/20 p-2"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          
          <CardHeader className="space-y-1">
            <div className="flex justify-center mb-4">
              <Link to="/" className="text-2xl font-bold text-solr-starfield-white">
                SOLR
              </Link>
            </div>
            <CardTitle className="text-2xl text-center text-solr-starfield-white">
              {isForgot ? 'Reset password' : isLogin ? 'Welcome back' : 'Create account'}
            </CardTitle>
            <CardDescription className="text-center text-solr-starfield-white/70">
              {isForgot
                ? "Enter your email and we'll send you a reset link"
                : isLogin
                ? 'Sign in to your account'
                : 'Join the SOLR community'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={isForgot ? handleForgot : handleSubmit} className="space-y-4">
              {!isLogin && !isForgot && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName" className="text-solr-starfield-white">
                        First name
                      </Label>
                        <Input
                          id="firstName"
                          name="firstName"
                          type="text"
                          placeholder="John"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          required
                          className="bg-white/90 border-solr-nebula-purple text-black placeholder:text-gray-500 focus:bg-white"
                        />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName" className="text-solr-starfield-white">
                        Last name
                      </Label>
                        <Input
                          id="lastName"
                          name="lastName"
                          type="text"
                          placeholder="Doe"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          required
                          className="bg-white/90 border-solr-nebula-purple text-black placeholder:text-gray-500 focus:bg-white"
                        />
                    </div>
                  </div>
                  <p className="text-xs text-solr-starfield-white/60 leading-relaxed pt-1">
                    Want to become a SOLR Ambassador? Create your customer account first, then apply from your dashboard — our team reviews every application.
                  </p>
                </>
              )}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-solr-starfield-white">
                  Email
                </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="name@example.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="bg-white/90 border-solr-nebula-purple text-black placeholder:text-gray-500 focus:bg-white"
                  />
              </div>
              {!isForgot && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-solr-starfield-white">
                      Password
                    </Label>
                    {isLogin && (
                      <button
                        type="button"
                        onClick={() => setMode('forgot')}
                        className="text-xs text-solr-nebula-purple hover:text-solr-nebula-purple/80"
                      >
                        Forgot password?
                      </button>
                    )}
                  </div>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    className="bg-white/90 border-solr-nebula-purple text-black focus:bg-white"
                  />
                </div>
              )}
              <Button
                type="submit"
                className="w-full bg-solr-nebula-purple hover:bg-solr-nebula-purple/80 text-solr-starfield-white disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    {isForgot ? 'Sending…' : isLogin ? 'Signing In...' : 'Creating Account...'}
                  </div>
                ) : (
                  isForgot ? 'Send reset link' : isLogin ? 'Sign In' : 'Create Account'
                )}
              </Button>
            </form>

            <div className="mt-4 text-center">
              {isForgot ? (
                <button
                  type="button"
                  onClick={() => setMode('login')}
                  className="text-solr-nebula-purple hover:text-solr-nebula-purple/80 text-sm"
                >
                  Back to sign in
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setMode(isLogin ? 'signup' : 'login')}
                  className="text-solr-nebula-purple hover:text-solr-nebula-purple/80 text-sm"
                >
                  {isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
                </button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Auth;